import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  setReplyingTo as setReplyingToAction,
  setEditingMessage as setEditingMessageAction,
  setForwardingMessage as setForwardingMessageAction,
  setHighlightedMessage,
} from '../../../redux/slices/chatSlice';


/**
 * UI State Context
 * Handles ephemeral UI state: reply, edit, forward, highlight
 */
const UIStateContext = createContext(null);

export { UIStateContext };

export const useUIState = () => {
  const ctx = useContext(UIStateContext);
  if (!ctx) {
    throw new Error('useUIState must be used within UIStateProvider');
  }
  return ctx;
};

export const UIStateProvider = ({ children }) => {
  const dispatch = useDispatch();

  // Redux Selectors
  const replyingTo = useSelector(state => state.chat.replyingTo);
  const editingMessage = useSelector(state => state.chat.editingMessage);
  const forwardingMessage = useSelector(state => state.chat.forwardingMessage);
  const highlightedMessageId = useSelector(state => state.chat.highlightedMessageId);

  // Actions
  const setReplyingTo = useCallback((val) => {
    dispatch(setReplyingToAction(val));
  }, [dispatch]);

  const setEditingMessage = useCallback((val) => {
    dispatch(setEditingMessageAction(val));
  }, [dispatch]);

  const setForwardingMessage = useCallback((val) => {
    dispatch(setForwardingMessageAction(val));
  }, [dispatch]);

  const setHighlightedMessageIdCallback = useCallback((val) => {
    dispatch(setHighlightedMessage(val));
  }, [dispatch]);

  // FIX D6: Wrap context helper closures in useCallback to prevent re-creation
  // ponytail: simple one-liners, native React feature, prevents unnecessary re-renders
  const clearReply = useCallback(() => setReplyingTo(null), [setReplyingTo]);
  const clearEdit = useCallback(() => setEditingMessage(null), [setEditingMessage]);
  const clearForward = useCallback(() => setForwardingMessage(null), [setForwardingMessage]);
  const isReplying = useCallback(() => replyingTo !== null, [replyingTo]);
  const isEditing = useCallback(() => editingMessage !== null, [editingMessage]);
  const isForwarding = useCallback(() => forwardingMessage !== null, [forwardingMessage]);
  const hasHighlight = useCallback(() => highlightedMessageId !== null, [highlightedMessageId]);

  const value = useMemo(() => ({
    // State
    replyingTo,
    editingMessage,
    forwardingMessage,
    highlightedMessageId,

    // Actions
    setReplyingTo,
    setEditingMessage,
    setForwardingMessage,
    setHighlightedMessage: setHighlightedMessageIdCallback,

    // Helpers
    clearReply,
    clearEdit,
    clearForward,
    isReplying,
    isEditing,
    isForwarding,
    hasHighlight,
  }), [
    replyingTo,
    editingMessage,
    forwardingMessage,
    highlightedMessageId,
    setReplyingTo,
    setEditingMessage,
    setForwardingMessage,
    setHighlightedMessageIdCallback,
    clearReply,
    clearEdit,
    clearForward,
    isReplying,
    isEditing,
    isForwarding,
    hasHighlight,
  ]);

  return (
    <UIStateContext.Provider value={value}>
      {children}
    </UIStateContext.Provider>
  );
};

export default UIStateProvider;
